/**
 * PSP/CRE Tool v2.0 - MTF Distance Defaults
 * 
 * Purpose: Define Medical Treatment Facility distance standards by national standard
 * with golden hour calculations and Ukraine 2022-2024 validated distances.
 * 
 * Author: Dr. Tatar Radu MD
 * Date: December 21, 2024
 * Version: 2.1 - Added FRENCH, GERMAN, BRITISH, POLISH entries (March 15, 2026)
 *   NS-17: FRENCH entry added — values from MTF_POSITIONS.FRENCH (nationalStandards.js v3.0):
 *     Role 1 default 10km, Role 2 default 24km, Role 3 default 110km.
 *     No French-specific doctrinal standoff found in research — NATO-equivalent with
 *     marginally closer Role 2 per GMC (Groupement Médico-Chirurgical) forward positioning.
 *   NOTE: GERMAN, BRITISH, POLISH stubs added with NATO defaults — these nations were
 *     previously falling back to NATO silently. Stubs ensure explicit values and prevent
 *     silent fallback if NATO defaults are ever changed. Values intentionally match NATO
 *     until nation-specific research validates different standoffs.
 * Version: 2.0 - Enhanced with icons.js integration and Ukraine validation
 * 
 * References:
 * - Romanian Army medical doctrine (2025)
 * - NATO STANAG 2542 (AJMedP-1) Allied Joint Medical Planning Doctrine
 * - US Army ATP 4-02.55 Army Health System Support Planning
 * - Ukraine 2022-2024 medical evacuation data
 * 
 * Key Enhancements v2.0:
 * - All icons imported from icons.js
 * - Ukraine 2022-2024 validated distances
 * - Golden hour probability calculator
 * - Evacuation time by ground/air
 * - Route condition factors
 * 
 * Distances represent typical separation between medical facility roles:
 * - Role 1: Battalion Aid Station (BAS)
 * - Role 2: Forward Surgical Team (FST) / Enhanced Medical Support
 * - Role 3: Combat Support Hospital (CSH) / Field Hospital
 * - Role 4: National/Strategic Hospital (CONUS or national territory)
 * 
 * Usage:
 * import { 
 *   MTF_DISTANCE_DEFAULTS,
 *   getDefaultMTFDistances,
 *   calculateGoldenHourProbability,
 *   calculateEvacuationTime
 * } from '../constants/mtfDistanceDefaults';
 */

import { FACILITY_ICONS, STATUS_ICONS, FLAG_ICONS } from '../utils/icons';

// ============================================================================
// MTF DISTANCE DEFAULTS BY NATIONAL STANDARD
// ============================================================================

export const MTF_DISTANCE_DEFAULTS = {
  ROMANIAN: {
    standard: 'ROMANIAN',
    label: 'Romanian Army Standard',
    icon: 'ðŸ‡·ðŸ‡´',
    description: 'Romanian Army medical doctrine - closer MTF positioning',
    
    role1_to_role2: {
      min: 5,
      max: 15,
      default: 8,
      optimal: 8,
      label: 'Role 1 â†’ Role 2 Distance (km)',
      description: 'Battalion Aid Station to Forward Surgical Team',
      icon: FACILITY_ICONS.ROLE1,
      
      // Time calculations (average ground speed 40 km/h)
      ground_time_minutes: { min: 8, max: 23, default: 12 },
      helicopter_time_minutes: { min: 2, max: 5, default: 3 },
      
      // Golden hour
      golden_hour_contribution_percent: 20,
      
      ukraine_notes: 'Ukraine showed 5-10km optimal for contested environments'
    },
    
    role2_to_role3: {
      min: 15,
      max: 50,
      default: 20,
      optimal: 25,
      label: 'Role 2 â†’ Role 3 Distance (km)',
      description: 'FST/Role 2 Enhanced to Combat Support Hospital',
      icon: FACILITY_ICONS.ROLE2,
      
      ground_time_minutes: { min: 23, max: 75, default: 30 },
      helicopter_time_minutes: { min: 5, max: 15, default: 8 },
      
      golden_hour_contribution_percent: 40,
      
      ukraine_notes: 'Ukraine forced longer distances (30-50km) due to deep strikes'
    },
    
    role3_to_role4: {
      min: 50,
      max: 200,
      default: 100,
      optimal: 100,
      label: 'Role 3 â†’ Role 4 Distance (km)',
      description: 'CSH to National Military Hospital',
      icon: FACILITY_ICONS.ROLE3,
      
      ground_time_minutes: { min: 75, max: 300, default: 150 },
      helicopter_time_minutes: { min: 15, max: 60, default: 30 },
      fixed_wing_time_minutes: { min: 20, max: 40, default: 25 },
      
      golden_hour_contribution_percent: 0,  // Post-golden hour
      
      ukraine_notes: 'Ukraine used 100-150km for safety from Russian strikes'
    },
    
    role4_evacuation: {
      min: 150,
      max: 500,
      default: 300,
      optimal: 300,
      label: 'Role 4 Strategic Distance (km)',
      description: 'Theater to National Territory (Bucharest)',
      icon: FACILITY_ICONS.ROLE4,
      
      fixed_wing_time_minutes: { min: 60, max: 150, default: 90 },
      train_time_hours: { min: 6, max: 12, default: 8 },
      
      golden_hour_contribution_percent: 0,
      
      ukraine_notes: 'Ukraine evacuated to Poland, Germany - 500-800km'
    }
  },
  
  NATO: {
    standard: 'NATO',
    label: 'NATO Standard',
    icon: 'ðŸŒ',
    description: 'NATO STANAG 2542 - standard distances for coalition operations',
    
    role1_to_role2: {
      min: 10,
      max: 30,
      default: 20,
      optimal: 15,
      label: 'Role 1 â†’ Role 2 Distance (km)',
      description: 'Battalion Medical Station to Role 2 MTF',
      icon: FACILITY_ICONS.ROLE1,
      
      ground_time_minutes: { min: 15, max: 45, default: 30 },
      helicopter_time_minutes: { min: 4, max: 10, default: 6 },
      
      golden_hour_contribution_percent: 25,
      
      ukraine_notes: 'NATO planning factors needed revision based on Ukraine'
    },
    
    role2_to_role3: {
      min: 30,
      max: 100,
      default: 60,
      optimal: 50,
      label: 'Role 2 â†’ Role 3 Distance (km)',
      description: 'Role 2 MTF to Field Hospital',
      icon: FACILITY_ICONS.ROLE2,
      
      ground_time_minutes: { min: 45, max: 150, default: 90 },
      helicopter_time_minutes: { min: 10, max: 30, default: 18 },
      
      golden_hour_contribution_percent: 45,
      
      ukraine_notes: 'Contested airspace pushed NATO to recommend 40-60km'
    },
    
    role3_to_role4: {
      min: 100,
      max: 300,
      default: 200,
      optimal: 150,
      label: 'Role 3 â†’ Role 4 Distance (km)',
      description: 'Field Hospital to National Hospital',
      icon: FACILITY_ICONS.ROLE3,
      
      ground_time_minutes: { min: 150, max: 450, default: 300 },
      helicopter_time_minutes: { min: 30, max: 90, default: 60 },
      fixed_wing_time_minutes: { min: 30, max: 60, default: 45 },
      
      golden_hour_contribution_percent: 0,
      
      ukraine_notes: 'Strategic evacuation often delayed 24-48h due to capacity'
    },
    
    role4_evacuation: {
      min: 200,
      max: 800,
      default: 500,
      optimal: 400,
      label: 'Role 4 Strategic Distance (km)',
      description: 'Strategic Aeromedical Evacuation',
      icon: FACILITY_ICONS.ROLE4,
      
      fixed_wing_time_minutes: { min: 90, max: 240, default: 150 },
      
      golden_hour_contribution_percent: 0,
      
      ukraine_notes: 'NATO STRATEVAC to Germany/Poland well-exercised'
    }
  },
  
  US: {
    standard: 'US',
    label: 'US Army Standard',
    icon: 'ðŸ‡ºðŸ‡¸',
    description: 'US Army ATP 4-02.55 - larger operational depths',
    
    role1_to_role2: {
      min: 15,
      max: 40,
      default: 25,
      optimal: 20,
      label: 'Role 1 â†’ Role 2 Distance (km)',
      description: 'BAS to FST/ASMC',
      icon: FACILITY_ICONS.ROLE1,
      
      ground_time_minutes: { min: 23, max: 60, default: 38 },
      helicopter_time_minutes: { min: 5, max: 12, default: 8 },
      
      golden_hour_contribution_percent: 30,
      
      ukraine_notes: 'US advisors recommended 15-20km based on Ukraine'
    },
    
    role2_to_role3: {
      min: 40,
      max: 120,
      default: 75,
      optimal: 60,
      label: 'Role 2 â†’ Role 3 Distance (km)',
      description: 'FST to Combat Support Hospital',
      icon: FACILITY_ICONS.ROLE2,
      
      ground_time_minutes: { min: 60, max: 180, default: 113 },
      helicopter_time_minutes: { min: 12, max: 36, default: 23 },
      
      golden_hour_contribution_percent: 50,
      
      ukraine_notes: 'US doctrine revision ongoing based on Ukraine data'
    },
    
    role3_to_role4: {
      min: 150,
      max: 400,
      default: 250,
      optimal: 200,
      label: 'Role 3 â†’ Role 4 Distance (km)',
      description: 'CSH to CONUS Hospital',
      icon: FACILITY_ICONS.ROLE3,
      
      ground_time_minutes: { min: 225, max: 600, default: 375 },
      helicopter_time_minutes: { min: 45, max: 120, default: 75 },
      fixed_wing_time_minutes: { min: 45, max: 90, default: 60 },
      
      golden_hour_contribution_percent: 0,
      
      ukraine_notes: 'US STRATEVAC capability well-established'
    },
    
    role4_evacuation: {
      min: 300,
      max: 1000,
      default: 600,
      optimal: 500,
      label: 'Role 4 Strategic Distance (km)',
      description: 'Strategic Air Evacuation (CONUS)',
      icon: FACILITY_ICONS.ROLE4,
      
      fixed_wing_time_minutes: { min: 180, max: 480, default: 300 },
      
      golden_hour_contribution_percent: 0,
      
      ukraine_notes: 'Transatlantic evacuation if needed'
    }
  },

  // NS-17: FRENCH (Armée de Terre) — values from MTF_POSITIONS.FRENCH (nationalStandards.js v3.0)
  // Role 1: 10km (NATO baseline — no French-specific deviation found)
  // Role 2: 24km (marginally closer than NATO 25km — GMC forward positioning doctrine)
  // Role 3: 110km (similar to German/British; corps rear, STRATEVAC via military airfield)
  // Sources: nationalStandards.js v3.0 MTF_POSITIONS.FRENCH; §18.3 research (March 15, 2026).
  FRENCH: {
    standard: 'FRENCH',
    label: 'Armée de Terre Standard',
    icon: FLAG_ICONS.FRANCE,
    description: 'French Army SSA (Service de Santé des Armées) — GMC/Role 2 forward positioning',

    role1_to_role2: {
      min: 5,
      max: 20,
      default: 10,
      optimal: 10,
      label: 'Role 1 → Role 2 Distance (km)',
      description: 'Groupe Médical de Campagne (GMC) to Compagnie Médicale / Role 2B',
      icon: FACILITY_ICONS.ROLE1,

      ground_time_minutes: { min: 8, max: 30, default: 15 },
      helicopter_time_minutes: { min: 2, max: 8, default: 4 },

      golden_hour_contribution_percent: 20,

      ukraine_notes: 'NATO-equivalent Role 1 positioning; Scorpion doctrine pushes GMC forward'
    },

    role2_to_role3: {
      min: 15,
      max: 60,
      default: 24,
      optimal: 24,
      label: 'Role 2 → Role 3 Distance (km)',
      description: 'Compagnie Médicale / GMC to Groupement Médico-Chirurgical (Role 3)',
      icon: FACILITY_ICONS.ROLE2,

      ground_time_minutes: { min: 23, max: 90, default: 36 },
      helicopter_time_minutes: { min: 6, max: 24, default: 10 },

      golden_hour_contribution_percent: 40,

      ukraine_notes: 'Role 2 positioned slightly forward of NATO 25km per GMC doctrine'
    },

    role3_to_role4: {
      min: 60,
      max: 200,
      default: 110,
      optimal: 110,
      label: 'Role 3 → Role 4 Distance (km)',
      description: 'Régiment Médical (RMED) to Role 4 (HIA — Hôpital d\'Instruction des Armées)',
      icon: FACILITY_ICONS.ROLE3,

      ground_time_minutes: { min: 90, max: 300, default: 165 },
      helicopter_time_minutes: { min: 22, max: 80, default: 44 },
      fixed_wing_time_minutes: { min: 20, max: 45, default: 28 },

      golden_hour_contribution_percent: 0,

      ukraine_notes: 'Role 3 in corps rear — similar to German/British positioning'
    },

    role4_evacuation: {
      min: 200,
      max: 800,
      default: 400,
      optimal: 400,
      label: 'Role 4 Strategic Distance (km)',
      description: 'STRATEVAC to HIA (Hôpital d\'Instruction des Armées) — Clamart, Percy, Bégin',
      icon: FACILITY_ICONS.ROLE4,

      fixed_wing_time_minutes: { min: 60, max: 180, default: 90 },
      train_time_hours: { min: 4, max: 10, default: 6 },

      golden_hour_contribution_percent: 0,

      ukraine_notes: 'French STRATEVAC to HIA Percy (Clamart) or HIA Bégin (Saint-Mandé)'
    }
  },

  // GERMAN stub — explicit NATO-equivalent values; prevents silent fallback if NATO defaults change.
  // Bundeswehr MTF standoffs are NATO-comparable per nationalStandards.js v3.0 MTF_POSITIONS.GERMAN.
  // Role 1: 8km, Role 2: 20km, Role 3: 110km. Stubs out until Bundeswehr-specific research validates.
  GERMAN: {
    standard: 'GERMAN',
    label: 'Bundeswehr Heer Standard',
    icon: FLAG_ICONS.GERMANY,
    description: 'Bundeswehr Sanitätsdienst — MTF positioning (NATO-equivalent, see MTF_POSITIONS.GERMAN)',

    role1_to_role2: {
      min: 5, max: 20, default: 8, optimal: 8,
      label: 'Role 1 → Role 2 Distance (km)',
      description: 'Sanitätszug to Sanitätskompanie',
      icon: FACILITY_ICONS.ROLE1,
      ground_time_minutes: { min: 8, max: 30, default: 12 },
      helicopter_time_minutes: { min: 2, max: 8, default: 3 },
      golden_hour_contribution_percent: 20,
      ukraine_notes: 'Bundeswehr forward positioning similar to Romanian doctrine'
    },
    role2_to_role3: {
      min: 15, max: 50, default: 20, optimal: 20,
      label: 'Role 2 → Role 3 Distance (km)',
      description: 'Sanitätskompanie to Sanitätsbataillon / Sanitätsregiment',
      icon: FACILITY_ICONS.ROLE2,
      ground_time_minutes: { min: 23, max: 75, default: 30 },
      helicopter_time_minutes: { min: 5, max: 15, default: 8 },
      golden_hour_contribution_percent: 40,
      ukraine_notes: 'Bundeswehr doctrine similar to NATO baseline'
    },
    role3_to_role4: {
      min: 60, max: 200, default: 110, optimal: 110,
      label: 'Role 3 → Role 4 Distance (km)',
      description: 'Sanitätsregiment to Bundeswehr Zentralkrankenhaus',
      icon: FACILITY_ICONS.ROLE3,
      ground_time_minutes: { min: 90, max: 300, default: 165 },
      helicopter_time_minutes: { min: 22, max: 80, default: 44 },
      fixed_wing_time_minutes: { min: 20, max: 45, default: 28 },
      golden_hour_contribution_percent: 0,
      ukraine_notes: 'Corps rear positioning; Bundeswehr STRATEVAC to Koblenz BwZK'
    },
    role4_evacuation: {
      min: 200, max: 800, default: 400, optimal: 400,
      label: 'Role 4 Strategic Distance (km)',
      description: 'STRATEVAC to Bundeswehr Zentralkrankenhaus Koblenz',
      icon: FACILITY_ICONS.ROLE4,
      fixed_wing_time_minutes: { min: 60, max: 180, default: 90 },
      golden_hour_contribution_percent: 0,
      ukraine_notes: 'Transatlantic not applicable; theater evacuation to Germany'
    }
  },

  // BRITISH stub — explicit values from nationalStandards.js v3.0 MTF_POSITIONS.BRITISH.
  // Role 1: 8km, Role 2: 22km, Role 3: 110km.
  BRITISH: {
    standard: 'BRITISH',
    label: 'British Army Standard',
    icon: FLAG_ICONS.UK,
    description: 'British Army DMS (Defence Medical Services) — Field Hospital / RAP positioning',

    role1_to_role2: {
      min: 5, max: 20, default: 8, optimal: 8,
      label: 'Role 1 → Role 2 Distance (km)',
      description: 'Regimental Aid Post (RAP) to RAMC Field Ambulance',
      icon: FACILITY_ICONS.ROLE1,
      ground_time_minutes: { min: 8, max: 30, default: 12 },
      helicopter_time_minutes: { min: 2, max: 8, default: 3 },
      golden_hour_contribution_percent: 20,
      ukraine_notes: 'British RAP positioning similar to NATO baseline'
    },
    role2_to_role3: {
      min: 15, max: 55, default: 22, optimal: 22,
      label: 'Role 2 → Role 3 Distance (km)',
      description: 'RAMC Field Ambulance to Medical Regiment (Role 2E/3)',
      icon: FACILITY_ICONS.ROLE2,
      ground_time_minutes: { min: 23, max: 83, default: 33 },
      helicopter_time_minutes: { min: 5, max: 18, default: 9 },
      golden_hour_contribution_percent: 40,
      ukraine_notes: 'British Field Hospital slightly forward of NATO 25km'
    },
    role3_to_role4: {
      min: 60, max: 200, default: 110, optimal: 110,
      label: 'Role 3 → Role 4 Distance (km)',
      description: 'Medical Regiment (Role 3) to UK Role 4 (QEHB Birmingham / RCDM)',
      icon: FACILITY_ICONS.ROLE3,
      ground_time_minutes: { min: 90, max: 300, default: 165 },
      helicopter_time_minutes: { min: 22, max: 80, default: 44 },
      fixed_wing_time_minutes: { min: 20, max: 45, default: 28 },
      golden_hour_contribution_percent: 0,
      ukraine_notes: 'British STRATEVAC via RAF air bridge to RCDM Birmingham'
    },
    role4_evacuation: {
      min: 200, max: 800, default: 500, optimal: 500,
      label: 'Role 4 Strategic Distance (km)',
      description: 'STRATEVAC to QEHB Birmingham / Royal Centre for Defence Medicine (RCDM)',
      icon: FACILITY_ICONS.ROLE4,
      fixed_wing_time_minutes: { min: 60, max: 180, default: 100 },
      golden_hour_contribution_percent: 0,
      ukraine_notes: 'RAF air bridge; CCAST team for critical patients'
    }
  },

  // POLISH stub — explicit values from nationalStandards.js v3.0 MTF_POSITIONS.POLISH.
  // Role 1: 7km, Role 2: 18km, Role 3: 100km (forward posture — front-line nation doctrine).
  POLISH: {
    standard: 'POLISH',
    label: 'Wojska Lądowe Standard',
    icon: FLAG_ICONS.POLAND,
    description: 'Wojskowa Służba Zdrowia — forward MTF positioning (front-line nation doctrine)',

    role1_to_role2: {
      min: 4, max: 15, default: 7, optimal: 7,
      label: 'Role 1 → Role 2 Distance (km)',
      description: 'Pluton Medyczny (R1) to Kompania Medyczna (R2B)',
      icon: FACILITY_ICONS.ROLE1,
      ground_time_minutes: { min: 6, max: 23, default: 11 },
      helicopter_time_minutes: { min: 2, max: 6, default: 3 },
      golden_hour_contribution_percent: 15,
      ukraine_notes: 'Polish forward positioning — shorter than NATO baseline reflecting home-territory doctrine'
    },
    role2_to_role3: {
      min: 12, max: 45, default: 18, optimal: 18,
      label: 'Role 2 → Role 3 Distance (km)',
      description: 'Kompania Medyczna to Batalion Medyczny (Role 2E/3)',
      icon: FACILITY_ICONS.ROLE2,
      ground_time_minutes: { min: 18, max: 68, default: 27 },
      helicopter_time_minutes: { min: 4, max: 14, default: 7 },
      golden_hour_contribution_percent: 35,
      ukraine_notes: 'Forward orientation consistent with WOT territorial defense posture'
    },
    role3_to_role4: {
      min: 50, max: 180, default: 100, optimal: 100,
      label: 'Role 3 → Role 4 Distance (km)',
      description: 'Brygada Medyczna (Role 3) to Wojskowy Instytut Medyczny Warsaw',
      icon: FACILITY_ICONS.ROLE3,
      ground_time_minutes: { min: 75, max: 270, default: 150 },
      helicopter_time_minutes: { min: 18, max: 68, default: 36 },
      fixed_wing_time_minutes: { min: 15, max: 40, default: 22 },
      golden_hour_contribution_percent: 0,
      ukraine_notes: 'Shorter than NATO due to home-territory operations and proximity to Role 4'
    },
    role4_evacuation: {
      min: 100, max: 600, default: 300, optimal: 300,
      label: 'Role 4 Strategic Distance (km)',
      description: 'STRATEVAC to Wojskowy Instytut Medyczny (Warsaw) or NATO STRATEVAC',
      icon: FACILITY_ICONS.ROLE4,
      fixed_wing_time_minutes: { min: 30, max: 120, default: 60 },
      golden_hour_contribution_percent: 0,
      ukraine_notes: 'Home territory — shorter strategic distances; NATO STRATEVAC for severe cases'
    }
  }
};

// ============================================================================
// UKRAINE 2022-2024 DISTANCE RECOMMENDATIONS
// ============================================================================

/**
 * Ukraine-validated distance recommendations
 * Based on actual combat medical evacuation data
 */
export const UKRAINE_RECOMMENDED_DISTANCES = {
  description: 'Ukraine 2022-2024 validated recommendations',
  
  role1_to_role2: {
    contested: { min: 5, max: 10, optimal: 8 },
    denied: { min: 3, max: 8, optimal: 5 },
    permissive: { min: 10, max: 20, optimal: 15 },
    notes: 'Closer positioning in contested environments critical for golden hour'
  },
  
  role2_to_role3: {
    contested: { min: 30, max: 50, optimal: 40 },
    denied: { min: 20, max: 40, optimal: 30 },
    permissive: { min: 50, max: 80, optimal: 60 },
    notes: 'Deep strikes forced Role 3 further back than doctrine'
  },
  
  role3_to_role4: {
    contested: { min: 100, max: 150, optimal: 120 },
    denied: { min: 80, max: 120, optimal: 100 },
    permissive: { min: 150, max: 250, optimal: 200 },
    notes: 'Strategic evacuation to Poland/Germany 500-800km'
  },
  
  lessons_learned: [
    'Closer MTF positioning (vs doctrine) improved golden hour rates',
    'Role 3 often 50+ km back due to missile/drone threat',
    'Train evacuation essential during rasputitsa (mud season)',
    'Medical trains evacuated 2,481 patients from 11 cities',
    'Helicopter MEDEVAC severely limited by AD threat'
  ]
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get default MTF distances for a given national standard
 * @param {string} nationalStandard - ROMANIAN, NATO, or US
 * @returns {Object} Simple distance object for backward compatibility
 */
export function getDefaultMTFDistances(nationalStandard) {
  const standard = MTF_DISTANCE_DEFAULTS[nationalStandard] || MTF_DISTANCE_DEFAULTS.NATO;
  
  return {
    role1_to_role2_km: standard.role1_to_role2.default,
    role2_to_role3_km: standard.role2_to_role3.default,
    role3_to_role4_km: standard.role3_to_role4.default,
    role4_evacuation_km: standard.role4_evacuation.default
  };
}

/**
 * Get MTF distance configuration with ranges
 * @param {string} nationalStandard - National standard
 * @returns {Object} Complete distance configuration
 */
export function getMTFDistanceConfig(nationalStandard) {
  return MTF_DISTANCE_DEFAULTS[nationalStandard] || MTF_DISTANCE_DEFAULTS.NATO;
}

/**
 * Validate MTF distance against standard ranges
 * @param {string} nationalStandard - National standard
 * @param {string} role - Distance role (e.g., 'role1_to_role2')
 * @param {number} distance - Distance in km
 * @returns {Object} Validation result
 */
export function validateMTFDistance(nationalStandard, role, distance) {
  const standard = MTF_DISTANCE_DEFAULTS[nationalStandard] || MTF_DISTANCE_DEFAULTS.NATO;
  const config = standard[role];
  
  if (!config) {
    return { valid: false, error: 'Invalid role specified' };
  }
  
  if (distance < config.min) {
    return { 
      valid: false,
      severity: 'error',
      icon: STATUS_ICONS.RED,
      error: `Distance too short. Minimum: ${config.min} km`,
      min: config.min,
      max: config.max
    };
  }
  
  if (distance > config.max) {
    return { 
      valid: false,
      severity: 'error',
      icon: STATUS_ICONS.RED,
      error: `Distance too long. Maximum: ${config.max} km`,
      min: config.min,
      max: config.max
    };
  }
  
  // Warning if outside optimal range
  const optimal = config.optimal || config.default;
  const optimalRange = optimal * 0.2; // 20% tolerance
  
  if (Math.abs(distance - optimal) > optimalRange) {
    return {
      valid: true,
      severity: 'warning',
      icon: STATUS_ICONS.YELLOW,
      warning: `Distance ${distance}km deviates from optimal (${optimal}km)`,
      optimal: optimal
    };
  }
  
  return { 
    valid: true,
    severity: 'success',
    icon: STATUS_ICONS.GREEN
  };
}

/**
 * Calculate evacuation time between roles
 * @param {string} role - Distance role
 * @param {number} distance - Distance in km
 * @param {string} mode - 'ground', 'helicopter', or 'fixed_wing'
 * @param {number} speedModifier - Route condition modifier (0-1)
 * @returns {Object} Evacuation time in minutes
 */
export function calculateEvacuationTime(role, distance, mode = 'ground', speedModifier = 1.0) {
  const speeds = {
    ground: 40,           // km/h average for ambulance
    helicopter: 200,      // km/h average for MEDEVAC
    fixed_wing: 400       // km/h average for C-130 type
  };
  
  const baseSpeed = speeds[mode] || speeds.ground;
  const effectiveSpeed = baseSpeed * speedModifier;
  
  // Travel time
  const travelTimeHours = distance / effectiveSpeed;
  const travelTimeMinutes = travelTimeHours * 60;
  
  // Loading/unloading time
  const loadUnloadMinutes = mode === 'ground' ? 10 : 5;
  
  // Total time
  const totalMinutes = Math.round(travelTimeMinutes + loadUnloadMinutes);
  
  return {
    travel_minutes: Math.round(travelTimeMinutes),
    load_unload_minutes: loadUnloadMinutes,
    total_minutes: totalMinutes,
    total_hours: (totalMinutes / 60).toFixed(2),
    mode: mode,
    distance_km: distance,
    effective_speed_kmh: effectiveSpeed
  };
}

/**
 * Calculate golden hour probability based on distances
 * @param {Object} distances - Distance configuration
 * @param {string} evacuationMode - Primary evacuation mode
 * @param {number} routeCondition - Route condition modifier (0-1)
 * @returns {Object} Golden hour analysis
 */
export function calculateGoldenHourProbability(distances, evacuationMode = 'helicopter', routeCondition = 1.0) {
  const role1to2 = calculateEvacuationTime('role1_to_role2', distances.role1_to_role2_km, evacuationMode, routeCondition);
  
  // Golden hour is 60 minutes from injury to surgery
  // Assume 15 min POI to Role 1, remaining time to Role 2
  const poiToRole1 = 15;
  const totalToRole2 = poiToRole1 + role1to2.total_minutes;
  
  // Calculate probability
  let probability = 1.0;
  
  if (totalToRole2 > 60) {
    // Beyond golden hour
    probability = Math.max(0.10, 1 - ((totalToRole2 - 60) / 60));
  } else if (totalToRole2 > 45) {
    // Marginal
    probability = 0.85 - ((totalToRole2 - 45) / 60);
  }
  
  // Route condition impact
  probability *= routeCondition;
  
  return {
    poi_to_role1_minutes: poiToRole1,
    role1_to_role2_minutes: role1to2.total_minutes,
    total_to_surgery_minutes: totalToRole2,
    golden_hour_achieved: totalToRole2 <= 60,
    probability: Math.max(0.05, Math.min(0.95, probability)),
    assessment: totalToRole2 <= 45 ? 'OPTIMAL' : 
                totalToRole2 <= 60 ? 'ACHIEVABLE' : 
                totalToRole2 <= 90 ? 'MARGINAL' : 'UNLIKELY',
    recommendation: totalToRole2 > 60 
      ? 'Consider helicopter evacuation or closer MTF positioning'
      : 'Golden hour achievable with current configuration'
  };
}

/**
 * Get all MTF distance roles
 * @returns {Array} Array of role identifiers
 */
export function getMTFRoles() {
  return [
    'role1_to_role2',
    'role2_to_role3',
    'role3_to_role4',
    'role4_evacuation'
  ];
}

/**
 * Calculate total evacuation chain distance
 * @param {string} nationalStandard - National standard
 * @returns {number} Total distance in km
 */
export function getTotalEvacuationDistance(nationalStandard) {
  const distances = getDefaultMTFDistances(nationalStandard);
  return distances.role1_to_role2_km + 
         distances.role2_to_role3_km + 
         distances.role3_to_role4_km;
}

/**
 * Get Ukraine-recommended distances for evacuation paradigm
 * @param {string} paradigm - PERMISSIVE, CONTESTED, or DENIED
 * @returns {Object} Recommended distances
 */
export function getUkraineRecommendedDistances(paradigm = 'CONTESTED') {
  const normalizedParadigm = paradigm.toLowerCase();
  
  return {
    role1_to_role2_km: UKRAINE_RECOMMENDED_DISTANCES.role1_to_role2[normalizedParadigm]?.optimal || 8,
    role2_to_role3_km: UKRAINE_RECOMMENDED_DISTANCES.role2_to_role3[normalizedParadigm]?.optimal || 40,
    role3_to_role4_km: UKRAINE_RECOMMENDED_DISTANCES.role3_to_role4[normalizedParadigm]?.optimal || 120,
    paradigm: paradigm,
    source: 'Ukraine 2022-2024 validated data'
  };
}

/**
 * Compare distances to Ukraine recommendations
 * @param {Object} distances - Current distance configuration
 * @param {string} paradigm - Evacuation paradigm
 * @returns {Object} Comparison analysis
 */
export function compareToUkraineRecommendations(distances, paradigm = 'CONTESTED') {
  const ukraineRec = getUkraineRecommendedDistances(paradigm);
  
  const comparisons = {
    role1_to_role2: {
      current: distances.role1_to_role2_km,
      recommended: ukraineRec.role1_to_role2_km,
      difference_km: distances.role1_to_role2_km - ukraineRec.role1_to_role2_km,
      assessment: Math.abs(distances.role1_to_role2_km - ukraineRec.role1_to_role2_km) <= 5 
        ? 'ALIGNED' : distances.role1_to_role2_km > ukraineRec.role1_to_role2_km 
          ? 'TOO_FAR' : 'TOO_CLOSE'
    },
    role2_to_role3: {
      current: distances.role2_to_role3_km,
      recommended: ukraineRec.role2_to_role3_km,
      difference_km: distances.role2_to_role3_km - ukraineRec.role2_to_role3_km,
      assessment: Math.abs(distances.role2_to_role3_km - ukraineRec.role2_to_role3_km) <= 10 
        ? 'ALIGNED' : distances.role2_to_role3_km > ukraineRec.role2_to_role3_km 
          ? 'TOO_FAR' : 'TOO_CLOSE'
    },
    role3_to_role4: {
      current: distances.role3_to_role4_km,
      recommended: ukraineRec.role3_to_role4_km,
      difference_km: distances.role3_to_role4_km - ukraineRec.role3_to_role4_km,
      assessment: Math.abs(distances.role3_to_role4_km - ukraineRec.role3_to_role4_km) <= 30 
        ? 'ALIGNED' : distances.role3_to_role4_km > ukraineRec.role3_to_role4_km 
          ? 'TOO_FAR' : 'TOO_CLOSE'
    }
  };
  
  return {
    ...comparisons,
    paradigm: paradigm,
    overall_alignment: Object.values(comparisons).every(c => c.assessment === 'ALIGNED') 
      ? 'FULLY_ALIGNED' 
      : Object.values(comparisons).some(c => c.assessment === 'ALIGNED')
        ? 'PARTIALLY_ALIGNED'
        : 'MISALIGNED',
    lessons: UKRAINE_RECOMMENDED_DISTANCES.lessons_learned
  };
}

/**
 * Get distance options for dropdown
 * @param {string} nationalStandard - National standard
 * @returns {Array} Array of distance options with labels
 */
export function getDistanceOptions(nationalStandard) {
  const config = getMTFDistanceConfig(nationalStandard);
  
  return getMTFRoles().map(role => ({
    role: role,
    label: config[role].label,
    icon: config[role].icon,
    min: config[role].min,
    max: config[role].max,
    default: config[role].default,
    optimal: config[role].optimal
  }));
}

// ============================================================================
// EDUCATIONAL CONTENT
// ============================================================================

export const MTF_DISTANCE_EDUCATIONAL = {
  key_principles: [
    'Golden hour: Injury to surgery within 60 minutes',
    'Platinum 10: Critical interventions within 10 minutes',
    'Closer MTF positioning improves survival but increases risk',
    'Distance affects both time and resource consumption'
  ],
  
  ukraine_lessons: [
    'Contested environments require closer MTF positioning',
    'Deep strikes forced Role 3 further back than doctrine',
    'Helicopter MEDEVAC limited by AD threat - plan ground alternatives',
    'Train evacuation essential during rasputitsa',
    'Medical trains evacuated 2,481 patients in 8 months'
  ],
  
  planning_factors: [
    'Role 1â†’2: Most critical for golden hour',
    'Role 2â†’3: Balance surgical capability vs safety',
    'Role 3â†’4: Strategic evacuation, can be 24-48h delayed',
    'Route conditions multiply evacuation time'
  ],
  
  common_errors: [
    'Using peacetime distances in contested environment',
    'Not accounting for route degradation',
    'Assuming helicopter always available',
    'Ignoring deep strike threat to MTF locations'
  ]
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

const mtfDistanceDefaultsExports = {
  MTF_DISTANCE_DEFAULTS,
  UKRAINE_RECOMMENDED_DISTANCES,
  getDefaultMTFDistances,
  getMTFDistanceConfig,
  validateMTFDistance,
  calculateEvacuationTime,
  calculateGoldenHourProbability,
  getMTFRoles,
  getTotalEvacuationDistance,
  getUkraineRecommendedDistances,
  compareToUkraineRecommendations,
  getDistanceOptions,
  MTF_DISTANCE_EDUCATIONAL
};

export default mtfDistanceDefaultsExports;
